import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import getPath from '../../assets/routes/paths';
import getLanguage from '../../services/helpers';
import {
  FILTER_PARAM_COUNTRY,
  FILTER_PARAM_COURSE,
  FILTER_PARAM_DISTRICT,
  FILTER_PARAM_ORDER_BY,
  useFilterParams
} from '../../utils/useFilterParams';

/**
 * Custom hook for handling suggestion selection in search components
 *
 * @param {Object} options - Configuration options
 * @param {Array} options.allCourses - All golf courses data
 * @param {Array} options.courses - Courses with path information
 * @returns {Function} handleSuggestionSelected function
 */
export const useSearchSuggestionHandler = ({ allCourses, courses, type }) => {
  const history = useHistory();
  const filterParams = useFilterParams();
  const languageCode = getLanguage();

  return useCallback(
    (suggestion) => {
      if (!suggestion) return;

      if (suggestion.type === 'course') {
        const clubId = suggestion.id;
        const course = courses.find((c) => c.id === clubId);

        if (type === 'contests') {
          history.push(`${getPath(languageCode, 'Upc')}${suggestion.name}`);
          return;
        }

        if (suggestion.participating === 1 && course?.path) {
          history.push(`${getPath(languageCode, 'GolfCourses')}${course.path}`);
          return;
        }

        const params = new URLSearchParams(filterParams);

        if (suggestion.participating === 1 && !course?.path) {
          console.warn(`Participating club ID: ${clubId} (${suggestion.golfclub}) is missing a path. Falling back to list view.`);
        }

        if (suggestion.golfclub) {
          params.set(FILTER_PARAM_COURSE, suggestion.golfclub);
        } else {
          params.delete(FILTER_PARAM_COURSE);
        }

        if (suggestion.abbr) {
          params.set(FILTER_PARAM_COUNTRY, suggestion.abbr);
        } else {
          params.delete(FILTER_PARAM_COUNTRY);
        }

        if (suggestion.dId) {
          params.set(FILTER_PARAM_DISTRICT, String(suggestion.dId));
        } else {
          params.delete(FILTER_PARAM_DISTRICT);
        }

        params.set(FILTER_PARAM_ORDER_BY, 'rating');
        history.push({
          pathname: getPath(languageCode, 'GolfCourses'),
          search: params.toString(),
          hash: 'mapList'
        });
        return;
      }

      const params = new URLSearchParams(filterParams);

      if (suggestion.type === 'country') {
        params.set(FILTER_PARAM_ORDER_BY, 'rating');
        params.set(FILTER_PARAM_COUNTRY, suggestion.countryAbbr);
        params.delete(FILTER_PARAM_DISTRICT);
        params.delete(FILTER_PARAM_COURSE);

        history.push({
          pathname: getPath(languageCode, 'GolfCourses'),
          search: params.toString(),
          hash: 'mapList'
        });
      } else if (suggestion.type === 'district') {
        params.set(FILTER_PARAM_ORDER_BY, 'rating');
        params.set(FILTER_PARAM_COUNTRY, suggestion.countryAbbr);
        params.set(FILTER_PARAM_DISTRICT, String(suggestion.id));
        params.delete(FILTER_PARAM_COURSE);

        history.push({
          pathname: getPath(languageCode, 'GolfCourses'),
          search: params.toString(),
          hash: 'mapList'
        });
      }
    },
    [history, filterParams, languageCode, allCourses, courses]
  );
};

export default useSearchSuggestionHandler;
